import { Tabs, Tab } from "@churchofjesuschrist/eden-tabs";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
    spacing16,
    spacing24,
    spacing32,
} from "@churchofjesuschrist/eden-style-constants";
import { Text3 } from "@churchofjesuschrist/eden-text";
import { useResizeObserver } from "@churchofjesuschrist/eden-resize-observer";
import Loading from "../Loading";
import { useSelectI18nStringById } from "../../../util/custom-hooks";

let Page, Document, pdfjs;
if (typeof window !== "undefined") {
    const reactPdf = require("react-pdf");
    Page = reactPdf.Page;
    Document = reactPdf.Document;
    pdfjs = reactPdf.pdfjs;
}

const options = {
    cMapUrl:
        typeof pdfjs !== "undefined"
            ? `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`
            : undefined,
};

const PaddedPage = styled.div`
    margin-top: ${spacing16};
    &:first-child {
        margin-top: 0;
    }
`;

const PDFPageWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    max-width: 100%;
    margin: ${spacing24} 0 0;
    height: auto;

    @media print {
        display: none;
    }
`;

const NoSheetMusicWrapper = styled.div`
    width: 100%;
    height: 50vh;
    max-height: 450px;
    border: 1px solid var(--borderColorSecondary);
    background: var(--grey2);
    text-align: center;
    padding: ${spacing32} ${spacing16};
    margin-top: ${spacing24};
    @media (prefers-color-scheme: dark) {
        background: var(--background-color-level3);
    }
`;

const StyledText3 = styled(Text3)`
    padding: ${spacing16} 0;
`;

const MusicTabWrapper = ({ children, pdfUrl }) => {
    const [numPages, setNumPages] = useState(null);
    const [width, setWidth] = useState(300);
    const [pdfWrapperWidth, setPdfWrapperWidth] = useState(0);
    const [isClient, setIsClient] = useState(false);
    const [activeTab, setActiveTab] = useState("lyrics-tab");
    // Track if the user has loaded the PDF on the current hymn so it doesn't reload between tab switches. It will reset when the user navigates.
    const [hasLoadedPdf, setHasLoadedPdf] = useState(false);

    const documentWrapperHook = useRef();

    const selectI18nStringById = useSelectI18nStringById();

    // Set client flag and worker source on client side only
    useEffect(() => {
        setIsClient(true);
        if (typeof window !== "undefined" && pdfjs) {
            pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }
    }, []);

    // Track when sheet music tab is activated for the first time
    useEffect(() => {
        if (activeTab === "sheet-music-tab" && !hasLoadedPdf) {
            setHasLoadedPdf(true);
        }
    }, [activeTab, hasLoadedPdf]);

    const onChange = useCallback(
        (entries) => {
            const wrapperWidth = entries[0].contentRect.width;
            if (entries[0].contentRect.width !== pdfWrapperWidth) {
                setPdfWrapperWidth(wrapperWidth);
            }
        },
        [pdfWrapperWidth],
    );

    useResizeObserver(onChange, documentWrapperHook);

    useEffect(() => {
        if (documentWrapperHook.current && pdfWrapperWidth > 0) {
            const wrapperWidth = documentWrapperHook.current.offsetWidth;
            setWidth(Math.min(wrapperWidth, 816) - 2); // Subtract 2 for the borders
        }
    }, [pdfWrapperWidth]);

    const ErrorElem = () => (
        <NoSheetMusicWrapper>
            <StyledText3>{selectI18nStringById("noPdfAvailable")}</StyledText3>
        </NoSheetMusicWrapper>
    );

    function onDocumentLoadSuccess({ numPages }) {
        if (documentWrapperHook.current) {
            setPdfWrapperWidth(documentWrapperHook.current.offsetWidth);
        }
        setNumPages(numPages);
    }

    function onLoadError() {
        return <ErrorElem />;
    }

    const onError = () => {
        return <ErrorElem />;
    };

    return (
        <Tabs data-music activeTab={activeTab} onChange={setActiveTab}>
            <Tab id="lyrics-tab" label={selectI18nStringById("lyricsOnly")}>
                {children}
            </Tab>
            <Tab
                id="sheet-music-tab"
                label={selectI18nStringById("pdfSheetMusic")}
            >
                {pdfUrl && isClient && hasLoadedPdf ? (
                    <PDFPageWrapper ref={documentWrapperHook} role="img">
                        <Document
                            aria-hidden="true"
                            file={pdfUrl}
                            options={options}
                            onLoadError={onLoadError}
                            onLoadSuccess={onDocumentLoadSuccess}
                            error={onError}
                            noData={onError}
                            loading={<Loading />}
                        >
                            {Array.from(new Array(numPages), (el, index) => (
                                <PaddedPage key={`${numPages} ${index + 1}`}>
                                    <Page
                                        width={width}
                                        pageNumber={index + 1}
                                        error={onError}
                                        noData={onError}
                                        devicePixelRatio={2}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        loading={<Loading />}
                                    />
                                </PaddedPage>
                            ))}
                        </Document>
                    </PDFPageWrapper>
                ) : pdfUrl && !isClient ? (
                    <Loading />
                ) : (
                    <ErrorElem />
                )}
            </Tab>
        </Tabs>
    );
};

export default MusicTabWrapper;
